/**
 * Pure-function HumanName formatter for STC `_person` docs.
 *
 * Extracted from `loaders.ts` so tests can exercise it without pulling
 * in the Firestore client init (which depends on esbuild's build-time
 * constants). The formatter handles BOTH FHIR R5 array-shaped HumanName
 * AND STC's dict-with-numeric-keys convention.
 *
 * Discovered 2026-05-23 from `_person/QqVAD7VekWJlgVwIUW9c`
 * (admin.monitor): every list-shaped FHIR field — `name`, `given`,
 * `identifier`, `telecom`, `address`, `link` — is stored as a dict
 * keyed by stringified numeric indices: `{"0": ..., "1": ...}`.
 * Plus the use of `"unused"` as a placeholder value in fields a person
 * doesn't actually have.
 *
 * Both shapes coexist in production data — older docs may have been
 * imported with arrays, newer docs (from OneTouch or hand-stub creation)
 * may use the dict shape. Renderer-side doesn't know or care.
 */
import type { DocumentSnapshot } from "firebase/firestore";

/**
 * Coerce a list-or-dict-with-numeric-keys to an array, preserving order.
 *
 * Sort by numeric key value (not lexicographically) so `"10"` comes
 * AFTER `"2"`, not before. Returns `[]` for null / non-object /
 * unrecognised shapes — caller iterates safely either way.
 */
function listOrDictToArray<T = unknown>(v: unknown): T[] {
  if (Array.isArray(v)) return v as T[];
  if (v !== null && typeof v === "object") {
    return Object.entries(v as Record<string, unknown>)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([, val]) => val as T);
  }
  return [];
}

/**
 * STC's `_person` docs carry `"unused"` as a placeholder value in
 * many fields (HumanName.given, address.line, telecom, etc.). Filter
 * these out so they don't render as part of the composed display name.
 */
const isMeaningful = (s: string): boolean => s !== "unused" && s.length > 0;

/**
 * Build a human-readable label from a `_person` doc.
 *
 * Precedence:
 *   1. `name[0].text` — preferred display string
 *   2. `name[0].given.join(" ") + " " + name[0].family` — composed
 *   3. `_displayName` — legacy fallback (not FHIR)
 *   4. `null` — caller renders the doc id as a `<code>` fallback
 *
 * "unused" placeholder values are skipped at every step.
 */
export function formatPersonName(
  doc: DocumentSnapshot | Record<string, unknown> | null | undefined,
): string | null {
  if (!doc) return null;
  const data: Record<string, unknown> =
    typeof (doc as DocumentSnapshot).data === "function"
      ? ((doc as DocumentSnapshot).data() as Record<string, unknown>)
      : (doc as Record<string, unknown>);
  if (!data) return null;

  const names = listOrDictToArray<{
    text?: unknown;
    family?: unknown;
    given?: unknown;
  }>(data.name);

  if (names.length > 0) {
    const first = names[0];

    if (typeof first?.text === "string" && isMeaningful(first.text)) {
      return first.text;
    }

    const givens = listOrDictToArray<unknown>(first?.given)
      .filter((g): g is string => typeof g === "string")
      .filter(isMeaningful);
    const family =
      typeof first?.family === "string" && isMeaningful(first.family)
        ? first.family
        : null;
    const composed = [...givens, family].filter(Boolean).join(" ");
    if (composed.length > 0) return composed;
  }

  if (typeof data._displayName === "string" && isMeaningful(data._displayName)) {
    return data._displayName;
  }
  return null;
}
